import React, { useCallback, useState, useRef, useEffect } from 'react';
import VideoService from '../../../../../../service/VideoService';
import { useLibraryContext } from '../../../../../../context/LibraryContext';
import useOutsideClick from '../../../../../../hooks/useOutsideClick';
import CardsList from '../../../../../../components/CardList/CardList';
import ListHeader from '../../../ListHeader/ListHeader';
import { t } from 'i18next';
import { ToastNotification } from '../../../../../../components/Toast/Toast';

const videoToDeleteInit = {
  uuid: '',
  videoIndex: -1,
  name: '',
};

function ArchiveCards(props) {
  const { searchInput, videos, listMode = false, setVideosAndTotalData, loadVideos } = props;
  const {
    selectedCard,
    toggleSelectedCard,
    toggleSelectedAllCard,
    isUnarchiveVideos,
    toggleUnarchive,
    toggleUnselectCards,
    isDeleteVideos,
    toggleDeleteVideos,
  } = useLibraryContext();

  const [confirming, setConfirming] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(videoToDeleteInit);
  const [showMenu, setShowMenu] = useState({});
  const mediaMatch = window.matchMedia('(max-width: 900px)');
  const [isMobileView] = useState(mediaMatch.matches);

  const menuRef = useRef(null);
  useOutsideClick(menuRef, () => {
    if (Object.keys(showMenu).length) {
      setShowMenu({});
    }
  });

  useEffect(() => {
    const unarchive = async () => {
      await unarchiveVideos();
      toggleUnarchive();
      toggleUnselectCards();
    };
    if (isUnarchiveVideos) {
      unarchive();
    }
  }, [isUnarchiveVideos]);

  const showConfirmationModal = useCallback((id, videoIndex, name) => {
    setConfirming(true);
    if (id !== '') {
      setVideoToDelete({
        id,
        videoIndex,
        name,
      });
    }
  }, []);

  useEffect(() => {
    const deleteVideos = async () => {
      setConfirming(true);
      await deleteVideoForever();
      toggleDeleteVideos();
    };

    if (isDeleteVideos) {
      deleteVideos();
    }
  }, [isDeleteVideos]);

  useEffect(() => {
    if (confirming) {
      deleteVideoForever();
    }
  }, [videoToDelete]);

  const unarchiveVideos = useCallback(
    async (uuid, videoIndex, name) => {
      try {
        const uuids = Object.keys(selectedCard);
        if (!uuid && uuids.length) {
          await Promise.all(uuids.map(uuid => VideoService.updateVideo(uuid, { archived: 0 })));
          ToastNotification({
            message: t('general.toast.unarchive_successfully_videos'),
            iconName: 'archive',
            isMobileView: isMobileView,
          });
          loadVideos();
          toggleUnselectCards();
        } else if (uuid) {
          await VideoService.updateVideo(uuid, { archived: 0 });
          setVideosAndTotalData(prevVideos => {
            const temp = [...prevVideos];
            temp.splice(videoIndex, 1);
            return temp;
          });
          ToastNotification({
            message: `${t('general.toast.unarchive_successfully')} '${name}'`,
            iconName: 'archive',
            isMobileView: isMobileView,
          });
        }
      } catch (e) {
        console.log('Error while unarchiveVideos', e);
      }
    },
    [setVideosAndTotalData, selectedCard]
  );

  const deleteVideoForever = useCallback(async () => {
    const { id, videoIndex, name } = videoToDelete;
    const confirmDelete = confirm(
      `Are you sure you want to permanently delete ${name ? `"${name}"` : 'the selected videos'}? This can’t be undone.`
    );
    if (confirmDelete) {
      try {
        if (videoIndex === -1) {
          const uuids = Object.keys(selectedCard);
          await Promise.all(
            uuids.map(id => {
              return VideoService.deleteVideoForever(id);
            })
          );
          ToastNotification({
            message: `${t('general.toast.delete_successfully_videos')}`,
            iconName: 'success',
            isMobileView: isMobileView,
          });
          setConfirming(false);
          setVideoToDelete(videoToDeleteInit);
          loadVideos();
          toggleUnselectCards();
        } else {
          await VideoService.deleteVideoForever(id);
          ToastNotification({
            message: `${t('general.toast.delete_successfully')} '${name}'`,
            iconName: 'success',
            isMobileView: isMobileView,
          });
          loadVideos();
          setConfirming(false);
          setVideoToDelete(videoToDeleteInit);
        }
      } catch (e) {
        Toast.error(e);
      }
    } else {
      setConfirming(false);
      setVideoToDelete(videoToDeleteInit);
    }
  }, [videoToDelete, showConfirmationModal, selectedCard, setVideosAndTotalData]);

  const handleCardClick = (event, videoIndex) => {
    const parentElement = event.target.parentElement.parentElement;
    if (
      parentElement.tagName !== 'BUTTON' &&
      parentElement.tagName !== 'LI' &&
      parentElement.tagName !== 'UL' &&
      event.target.tagName !== 'LI' &&
      event.target.tagName !== 'UL'
    ) {
      toggleSelectedCard(videos[videoIndex]);
    }
  };

  const handleDotsClick = id => {
    setShowMenu({
      uuid: id,
      flag: true,
    });
  };

  const handleAllCardClick = () => {
    toggleSelectedAllCard(videos);
  };

  useEffect(() => {
    return () => toggleUnselectCards();
  }, []);

  return (
    <>
      {listMode && videos.length > 0 && (
        <ListHeader isAllCardSelected={Object.keys(selectedCard).length === videos.length} handleAllCardClick={handleAllCardClick} />
      )}
      {videos && (
        <CardsList
          menuRef={menuRef}
          listMode={listMode}
          videos={videos}
          searchInput={searchInput}
          toggleShowMenu={() => setShowMenu({})}
          handleDotsClick={handleDotsClick}
          showMenu={showMenu}
          handleCardClick={handleCardClick}
          deleteVideo={showConfirmationModal}
          unarchiveVideo={unarchiveVideos}
          isMobileView={isMobileView}
          isShowApprovalStatus
        />
      )}
    </>
  );
}

export default ArchiveCards;
